import clientAxiosInstance from "./clientAxiosInstance";
import { normalizeToYmd, validateDobYmd } from "../utils/dateFieldConstraints";
import { isCustodialMinorOwner } from "../utils/memberLinks";
import { normalizeEmploymentInfoForStatus } from "../constants/membersConstants";
import { validateEmail as isValidEmail } from "../utils/validationHelpers";

// Validation functions
export const validateNumericField = (value, fieldName, min = 0, max = 999999999) => {
  if (value === null || value === undefined || value === '') return null;
  
  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return `${fieldName} must be a valid number`;
  }
  if (numValue < min) {
    return `${fieldName} cannot be less than ${min}`;
  }
  if (numValue > max) {
    return `${fieldName} cannot exceed ${max.toLocaleString()}`;
  }
  return null;
};

export const validateIntegerField = (value, fieldName, min = 0, max = 999999999) => {
  if (value === null || value === undefined || value === '') return null;
  
  const numValue = parseInt(value);
  if (isNaN(numValue)) {
    return `${fieldName} must be a valid number`;
  }
  if (numValue < min) {
    return `${fieldName} cannot be less than ${min}`;
  }
  if (numValue > max) {
    return `${fieldName} cannot exceed ${max.toLocaleString()}`;
  }
  return null;
};

export const validateEmail = (email) => {
  if (!email) return null;
  return isValidEmail(email) ? null : "Please enter a valid email address";
};

export const validateRequired = (value, fieldName) => {
  if (!value || value.toString().trim() === '') {
    return `${fieldName} is required`;
  }
  return null;
};

export const validateDate = (dateStr, fieldName) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) {
    return `${fieldName} must be a valid date`;
  }
  return null;
};

// Clean empty values - convert empty strings to null, but preserve 0 values
export const cleanValue = (value) => {
  if (value === '' || value === undefined) return null;
  return value;
};

// Helper function to normalize values for comparison
const normalizeForComparison = (val) => {
  if (val === null || val === undefined || val === '') return null;
  
  // Handle JSON strings - try to parse them
  if (typeof val === 'string' && (val.startsWith('{') || val.startsWith('['))) {
    try {
      return JSON.parse(val);
    } catch {
      return val;
    }
  }
  
  return val;
};

// Deep comparison function
const deepEqual = (val1, val2) => {
  val1 = normalizeForComparison(val1);
  val2 = normalizeForComparison(val2);
  
  if (val1 === val2) return true;
  
  if (Array.isArray(val1) && Array.isArray(val2)) {
    if (val1.length !== val2.length) return false;
    return val1.every((item, index) => deepEqual(item, val2[index]));
  }
  
  if (typeof val1 === 'object' && typeof val2 === 'object' && val1 !== null && val2 !== null) {
    const keys1 = Object.keys(val1);
    const keys2 = Object.keys(val2);
    if (keys1.length !== keys2.length) return false;
    return keys1.every(key => deepEqual(val1[key], val2[key]));
  }
  
  return false;
};

// Prepare household data for API - only include changed fields
export const prepareHouseholdData = (editedData, originalData) => {
  const household = {};
  
  // Simple fields to check
  const simpleFields = [
    'name', 'estimated_liquid_net_worth', 'estimated_total_net_worth',
    'annual_income', 'aum', 'tax_percentage'
  ];
  
  simpleFields.forEach(field => {
    if (!deepEqual(editedData[field], originalData[field])) {
      household[field] = cleanValue(editedData[field]);
    }
  });

  // JSON fields - if any nested property changed, send the complete object
  const jsonFields = [
    'tax_info', 'annual_expense_range', 'special_expense_range', 
    'investor_associations', 'investor_experience'
  ];
  
  jsonFields.forEach(field => {
    if (!deepEqual(editedData[field], originalData[field])) {
      if (editedData[field]) {
        household[field] = typeof editedData[field] === 'object' 
          ? editedData[field] 
          : JSON.parse(editedData[field]);
      }
    }
  });

  return household;
};

// Align with advisor MissingMemberFieldModal: backend expects "USA", not blank or "United States".
export const normalizeCountryOfCitizenshipInPersonalInfo = (personalInfo) => {
  if (!personalInfo || typeof personalInfo !== "object") return personalInfo;
  const out = { ...personalInfo };
  const current = String(out.country_of_citizenship || "").trim();
  if (current === "" || current === "United States") {
    out.country_of_citizenship = "USA";
  }
  return out;
};

const cloneMemberWithNormalizedCitizenship = (member) => {
  if (!member || member.personal_info === undefined || member.personal_info === null) {
    return member;
  }
  const m = { ...member };
  let pi = m.personal_info;
  if (typeof pi === "string") {
    try {
      pi = JSON.parse(pi || "{}");
    } catch {
      return member;
    }
  }
  if (typeof pi !== "object" || pi === null) return member;
  m.personal_info = normalizeCountryOfCitizenshipInPersonalInfo({ ...pi });
  return m;
};

// Prepare member data for API - only include changed members
export const prepareMemberData = (editedMembers, originalMembers) => {
  const householdMembers = {};
  
  editedMembers.forEach(editedMember => {
    const originalMember = originalMembers.find(m => m.id === editedMember.id);
    const editedForCompare = cloneMemberWithNormalizedCitizenship(editedMember);
    
    // If member doesn't exist in original or has changes, include it
    if (!originalMember || !deepEqual(editedForCompare, originalMember)) {
      const memberData = {};

      // Handle personal_info JSON field
      if (editedMember.personal_info) {
        const rawPi = typeof editedMember.personal_info === 'object'
          ? { ...editedMember.personal_info }
          : JSON.parse(editedMember.personal_info);
        memberData.personal_info = normalizeCountryOfCitizenshipInPersonalInfo(rawPi);
      }

      // Handle id_info JSON field
      if (editedMember.id_info) {
        memberData.id_info = typeof editedMember.id_info === 'object'
          ? editedMember.id_info
          : JSON.parse(editedMember.id_info);
      }

      // Handle employment_info JSON field
      if (editedMember.employment_info) {
        const rawEi =
          typeof editedMember.employment_info === "object"
            ? { ...editedMember.employment_info }
            : JSON.parse(editedMember.employment_info);
        memberData.employment_info =
          normalizeEmploymentInfoForStatus(rawEi);
      }

      householdMembers[editedMember.id] = memberData;
    }
  });

  return householdMembers;
};

// Prepare account data for API - only include changed accounts
export const prepareAccountData = (editedAccounts, originalAccounts) => {
  const accountsData = {};
  
  editedAccounts.forEach(editedAccount => {
    const originalAccount = originalAccounts.find(a => a.id === editedAccount.id);
    
    // If account doesn't exist in original or has changes, include it
    if (!originalAccount || !deepEqual(editedAccount, originalAccount)) {
      const accountData = {
        new_account_number: cleanValue(editedAccount.new_account_number),
        custodian: cleanValue(editedAccount.custodian),
        registration: cleanValue(editedAccount.registration),
        type: cleanValue(editedAccount.type),
        fee_billed_percentage: cleanValue(editedAccount.fee_billed_percentage),
        account_value: cleanValue(editedAccount.account_value),
        new_rep_id: cleanValue(editedAccount.new_rep_id),
        proceeds: cleanValue(editedAccount.proceeds),
        security_purchases: cleanValue(editedAccount.security_purchases),
        distribution_income: cleanValue(editedAccount.distribution_income),
        margin: Boolean(editedAccount.margin),
        options: Boolean(editedAccount.options),
        fee_based_account: Boolean(editedAccount.fee_based_account),
        check_writing: Boolean(editedAccount.check_writing)
      };

      const jsonFields = [
        'owners_info', 'transfer_info', 'beneficiaries', 'trusted_contacts',
        'risk_tolerance', 'bank_accounts', 'withdrawals', 'deposits', 'trust_info'
      ];

      jsonFields.forEach(field => {
        if (editedAccount[field]) {
          const parsed = typeof editedAccount[field] === 'object'
            ? editedAccount[field]
            : JSON.parse(editedAccount[field]);
          if (field === "bank_accounts" && Array.isArray(parsed)) {
            // Default bank account category to personal when unset, per element.
            parsed.forEach((ba) => {
              if (ba && typeof ba === "object" && (ba.is_personal === null || ba.is_personal === undefined)) {
                ba.is_personal = true;
              }
            });
          }
          accountData[field] = parsed;
        }
      });

      accountsData[editedAccount.id] = accountData;
    }
  });

  return accountsData;
};

const deepCleanSSNPhone = (obj) => {
  if (Array.isArray(obj)) {
    obj.forEach(item => deepCleanSSNPhone(item));
  } else if (obj !== null && typeof obj === 'object') {
    Object.keys(obj).forEach(key => {
      if (['ssn_or_tin', 'phone', 'ssn', 'tin'].includes(key) && typeof obj[key] === 'string' && !obj[key].includes('*')) {
        obj[key] = obj[key].replace(/\D/g, '');
      } else {
        deepCleanSSNPhone(obj[key]);
      }
    });
  }
};

// Main API call function
export const updateHouseholdAPI = async (household_id, clientToken, payload) => {
  try {
    // Deep copy payload to avoid mutating state
    const cleanPayload = JSON.parse(JSON.stringify(payload));
    deepCleanSSNPhone(cleanPayload);

    const response = await clientAxiosInstance.put(
      `/client/household/${household_id}`,
      cleanPayload,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${clientToken}`
        }
      }
    );
    
    // Handle success response: { "success": true, "message": "Household updated successfully" }
    if (response.data && response.data.success === true) {
      return { 
        success: true, 
        message: response.data.message || "Update successful",
        data: response.data 
      };
    }
    
    // If response doesn't have expected structure, still treat as success
    return { 
      success: true, 
      message: "Update successful",
      data: response.data 
    };
  } catch (error) {
    console.error("Update household error:", error);
    
    const status = error.response?.status;
    const errorDetail = error.response?.data?.detail;
    
    // Handle specific error cases
    if (status === 401) {
      // Session expired or invalid credentials
      if (errorDetail === "Session expired or invalid") {
        return {
          success: false,
          error: "Your session has expired. Please log in again.",
          sessionExpired: true
        };
      } else if (errorDetail === "Could not validate client credentials") {
        return {
          success: false,
          error: "Authentication failed. Please log in again.",
          sessionExpired: true
        };
      }
      return {
        success: false,
        error: errorDetail || "Authentication failed. Please log in again.",
        sessionExpired: true
      };
    }
    
    if (status === 500) {
      return {
        success: false,
        error: errorDetail || "Server error. Please try again later."
      };
    }
    
    // Handle other error status codes
    return {
      success: false,
      error: errorDetail || error.message || "Failed to update household data. Please try again."
    };
  }
};

// Validation functions for specific data types
export const validateHouseholdData = (data) => {
  const errors = [];

  // Validate numeric fields
  const numericValidations = [
    { field: data.estimated_total_net_worth, name: 'Total Net Worth' },
    { field: data.estimated_liquid_net_worth, name: 'Liquid Net Worth' },
    { field: data.annual_income, name: 'Annual Income' },
    { field: data.tax_percentage, name: 'Tax Percentage', min: 0, max: 100 }
  ];

  numericValidations.forEach(({ field, name, min = 0, max = 999999999 }) => {
    const error = validateNumericField(field, name, min, max);
    if (error) errors.push(error);
  });

  // Validate ranges
  if (data.annual_expense_range) {
    const range = typeof data.annual_expense_range === 'object' 
      ? data.annual_expense_range 
      : JSON.parse(data.annual_expense_range);
    
    const startError = validateNumericField(range.start, 'Annual Expense Range Start');
    const endError = validateNumericField(range.end, 'Annual Expense Range End');
    
    if (startError) errors.push(startError);
    if (endError) errors.push(endError);
    
    if (!startError && !endError && range.start > range.end) {
      errors.push('Annual Expense Range start cannot be greater than end');
    }
  }

  if (data.special_expense_range) {
    const range = typeof data.special_expense_range === 'object' 
      ? data.special_expense_range 
      : JSON.parse(data.special_expense_range);
    
    const startError = validateNumericField(range.start, 'Special Expense Range Start');
    const endError = validateNumericField(range.end, 'Special Expense Range End');
    
    if (startError) errors.push(startError);
    if (endError) errors.push(endError);
    
    if (!startError && !endError && range.start > range.end) {
      errors.push('Special Expense Range start cannot be greater than end');
    }
  }

  // Validate tax bracket
  if (data.tax_info && data.tax_info.tax_bracket) {
    const bracket = data.tax_info.tax_bracket;
    const startError = validateNumericField(bracket.start, 'Tax Bracket Start', 0, 100);
    const endError = validateNumericField(bracket.end, 'Tax Bracket End', 0, 100);
    
    if (startError) errors.push(startError);
    if (endError) errors.push(endError);
    
    if (!startError && !endError && bracket.start > bracket.end) {
      errors.push('Tax Bracket start cannot be greater than end');
    }
  }

  // Validate investment experience
  if (data.investor_experience) {
    const experience = typeof data.investor_experience === 'object' 
      ? data.investor_experience 
      : JSON.parse(data.investor_experience);
    
    Object.entries(experience).forEach(([key, value]) => {
      const error = validateIntegerField(value, `${key.replace(/_/g, ' ')} experience`, 0, 100);
      if (error) errors.push(error);
    });
  }

  return errors;
};

export const validateMemberData = (member, accounts = []) => {
  const errors = [];

  if (!member.personal_info) return errors;
  
  const personalInfo = typeof member.personal_info === 'object' 
    ? member.personal_info 
    : JSON.parse(member.personal_info);

  // Required fields
  const requiredError = validateRequired(personalInfo.first_name, 'First Name');
  if (requiredError) errors.push(requiredError);

  const lastNameError = validateRequired(personalInfo.last_name, 'Last Name');
  if (lastNameError) errors.push(lastNameError);

  // Email validation
  const emailError = validateEmail(personalInfo.email);
  if (emailError) errors.push(emailError);

  if (personalInfo.dob) {
    const effectivePrimary =
      member.is_primary === true &&
      !isCustodialMinorOwner(accounts, member.id);
    const dobErr = validateDobYmd(normalizeToYmd(personalInfo.dob), effectivePrimary);
    if (dobErr) errors.push(dobErr);
  }

  return errors;
};

export const BENEFICIARY_TOTAL_EPS = 0.01;

function parseAccountBeneficiariesList(account) {
  let list = account?.beneficiaries;
  if (list == null) return [];
  if (typeof list === "string") {
    try {
      list = JSON.parse(list);
    } catch {
      return [];
    }
  }
  return Array.isArray(list) ? list : [];
}

/**
 * Block save only when primary or contingent totals exceed 100%.
 * Under 100% is allowed; incomplete allocation is surfaced via API missing_fields / pending data.
 */
export function getBeneficiaryShareTotalErrors(account) {
  const errors = [];
  const list = parseAccountBeneficiariesList(account);
  if (list.length === 0) return errors;

  let primarySum = 0;
  let contingentSum = 0;
  let hasPrimary = false;
  let hasContingent = false;

  for (const b of list) {
    const t = String(b.type || "").toLowerCase();
    const sh = parseFloat(b.share);
    const num = Number.isFinite(sh) ? sh : 0;
    if (t === "primary") {
      hasPrimary = true;
      primarySum += num;
    } else if (t === "contingent") {
      hasContingent = true;
      contingentSum += num;
    }
  }

  const roundPct = (n) => Math.round(n * 100) / 100;
  if (hasPrimary && primarySum > 100 + BENEFICIARY_TOTAL_EPS) {
    errors.push(`Primary: ${roundPct(primarySum)}% (exceeds 100%).`);
  }
  if (hasContingent && contingentSum > 100 + BENEFICIARY_TOTAL_EPS) {
    errors.push(`Contingent: ${roundPct(contingentSum)}% (exceeds 100%).`);
  }
  return errors;
}

/**
 * Same rules as getBeneficiaryShareTotalErrors, merging per-index form changes and excluding removed beneficiary ids.
 * `beneficiaryFormChanges` is shaped like accountsFormData[accountId].beneficiaries: { 0: { type, share, ... }, ... }.
 */
export function getBeneficiaryShareTotalErrorsFromForm(account, beneficiaryFormChanges, removedBeneficiaryIds = []) {
  const errors = [];
  const list = parseAccountBeneficiariesList(account);
  if (list.length === 0) return errors;

  const removed = new Set(removedBeneficiaryIds || []);
  const form = beneficiaryFormChanges && typeof beneficiaryFormChanges === "object" ? beneficiaryFormChanges : {};

  let primarySum = 0;
  let contingentSum = 0;
  let hasPrimary = false;
  let hasContingent = false;

  list.forEach((b, idx) => {
    if (removed.has(b?.id)) return;
    const row = form[idx] ?? form[String(idx)] ?? {};
    const typeRaw = row.type !== undefined ? row.type : b.type;
    const t = String(typeRaw || "").toLowerCase();
    const shareRaw = row.share !== undefined ? row.share : b.share;
    const sh = parseFloat(shareRaw);
    const num = Number.isFinite(sh) ? sh : 0;
    if (t === "primary") {
      hasPrimary = true;
      primarySum += num;
    } else if (t === "contingent") {
      hasContingent = true;
      contingentSum += num;
    }
  });

  const roundPct = (n) => Math.round(n * 100) / 100;
  if (hasPrimary && primarySum > 100 + BENEFICIARY_TOTAL_EPS) {
    errors.push(`Primary: ${roundPct(primarySum)}% (exceeds 100%).`);
  }
  if (hasContingent && contingentSum > 100 + BENEFICIARY_TOTAL_EPS) {
    errors.push(`Contingent: ${roundPct(contingentSum)}% (exceeds 100%).`);
  }
  return errors;
}

export function isBeneficiaryShareTotalComplete(total) {
  return Math.abs(total - 100) < BENEFICIARY_TOTAL_EPS;
}

export function isBeneficiaryShareTotalOver100(total) {
  return total > 100 + BENEFICIARY_TOTAL_EPS;
}

const OWNER_VALIDATION_RULES = {
  "Individual": { min: 1, max: 1, primaryOnly: false },
  "Joint": { min: 2, max: 2, primaryOnly: false },
  "Retirement — IRA": { min: 1, max: 1, primaryOnly: false },
  "Retirement — ROTH IRA": { min: 1, max: 1, primaryOnly: false },
  "Retirement — Rollover IRA": { min: 1, max: 1, primaryOnly: false },
  "Trust": { min: 1, max: 2, primaryOnly: false },
  "Business": { min: 1, max: 4, primaryOnly: false },
  "UTMA/UGMA": { min: 2, max: 2, primaryOnly: false },
  "529": { min: 1, max: 1, primaryOnly: false },
};

export const validateAccountData = (account, members = []) => {
  const errors = [];

  // 1. Ownership Validation
  const registration = account.registration;
  const owners = account.owners_info || [];
  const rules = OWNER_VALIDATION_RULES[registration];

  if (rules) {
    const isNonProfitBusiness =
      registration === "Business" && account.business_info?.is_non_profit === true;
    const minOwners = isNonProfitBusiness ? 0 : rules.min;
    const numOwners = owners.length;
    if (numOwners < minOwners || numOwners > rules.max) {
      if (minOwners === rules.max) {
        errors.push(`${registration} accounts must have exactly ${rules.max} owner(s).`);
      } else {
        errors.push(`${registration} accounts must have between ${minOwners} and ${rules.max} owner(s).`);
      }
    }

    if (rules.primaryOnly) {
      const primaryMember = members.find(m => m.is_primary);
      if (primaryMember) {
        owners.forEach(owner => {
          if (String(owner.id) !== String(primaryMember.id)) {
            errors.push(`Only the Primary Household Member can be an owner for ${registration} accounts.`);
          }
        });
      }
    }
  }

  // 2. Validate numeric fields
  const numericValidations = [
    { field: account.account_value, name: 'Account Value' },
  ];

  numericValidations.forEach(({ field, name }) => {
    const error = validateNumericField(field, name);
    if (error) errors.push(error);
  });

  // 3. Validate risk tolerance time horizon
  if (account.risk_tolerance) {
    const riskTolerance = typeof account.risk_tolerance === 'object' 
      ? account.risk_tolerance 
      : JSON.parse(account.risk_tolerance);
    
    if (riskTolerance.time_horizon) {
      const startError = validateIntegerField(riskTolerance.time_horizon.start, 'Time Horizon Start', 0, 100);
      const endError = validateIntegerField(riskTolerance.time_horizon.end, 'Time Horizon End', 0, 100);
      
      if (startError) errors.push(startError);
      if (endError) errors.push(endError);
      
      if (!startError && !endError && riskTolerance.time_horizon.start > riskTolerance.time_horizon.end) {
        errors.push('Time Horizon start cannot be greater than end');
      }
    }
  }

  getBeneficiaryShareTotalErrors(account).forEach((e) => errors.push(e));

  return errors;
};
